export enum ContactWizardStep {
  Role = 1,
  Photos = 2,
  Identity = 3,
  Address = 4,
  Contact = 5,
  Review = 6,
}

export const CONTACT_WIZARD_STEP_ORDER: readonly ContactWizardStep[] = [
  ContactWizardStep.Role,
  ContactWizardStep.Photos,
  ContactWizardStep.Identity,
  ContactWizardStep.Address,
  ContactWizardStep.Contact,
  ContactWizardStep.Review,
];

export const CONTACT_WIZARD_STEP_COUNT = CONTACT_WIZARD_STEP_ORDER.length;

export const PERSON_TYPE_WITNESS = 'Witness';
export const PERSON_TYPE_SUSPECT = 'Suspect';
export const PERSON_TYPE_VICTIM = 'Victim';
export const PERSON_TYPE_PROPERTY_REP = 'PropertyRep';
export const PERSON_TYPE_OTHER = 'OtherPerson';

export interface RoleOption {
  readonly personType: string;
  readonly label: string;
  readonly hint: string;
  readonly icon: string;
  readonly hue: string;
}

export const ROLE_OPTIONS: readonly RoleOption[] = [
  { personType: PERSON_TYPE_WITNESS, label: 'Witness', hint: 'Saw or heard something relevant', icon: 'visibility', hue: 'green' },
  { personType: PERSON_TYPE_SUSPECT, label: 'Suspect', hint: 'Person of interest or accused', icon: 'warning_amber', hue: 'red' },
  { personType: PERSON_TYPE_VICTIM, label: 'Victim', hint: 'Person harmed or complainant', icon: 'medical_services', hue: 'blue' },
  { personType: PERSON_TYPE_PROPERTY_REP, label: 'Property representative', hint: 'Owner, manager or keyholder', icon: 'maps_home_work', hue: 'yellow' },
  { personType: PERSON_TYPE_OTHER, label: 'Other', hint: 'Anyone else connected to the case', icon: 'person', hue: 'purple' },
];

const STEP_TITLES: Readonly<Record<ContactWizardStep, string>> = {
  [ContactWizardStep.Role]: 'Who are you recording?',
  [ContactWizardStep.Photos]: 'Photos and ID',
  [ContactWizardStep.Identity]: 'Identity',
  [ContactWizardStep.Address]: 'Address',
  [ContactWizardStep.Contact]: 'Contact and notes',
  [ContactWizardStep.Review]: 'Review',
};

const OPTIONAL_STEPS: ReadonlySet<ContactWizardStep> = new Set([
  ContactWizardStep.Photos,
  ContactWizardStep.Address,
  ContactWizardStep.Contact,
]);

export function roleOption(personType: string): RoleOption | undefined {
  return ROLE_OPTIONS.find((option) => option.personType === personType);
}

export function roleLabel(personType: string): string {
  const option = roleOption(personType);
  return option ? option.label : personType;
}

export function stepTitle(step: ContactWizardStep): string {
  return STEP_TITLES[step];
}

export function stepNumber(step: ContactWizardStep): number {
  return CONTACT_WIZARD_STEP_ORDER.indexOf(step) + 1;
}

export function isOptionalStep(step: ContactWizardStep): boolean {
  return OPTIONAL_STEPS.has(step);
}

export function nextStep(step: ContactWizardStep): ContactWizardStep {
  const index = CONTACT_WIZARD_STEP_ORDER.indexOf(step);
  if (index < 0 || index === CONTACT_WIZARD_STEP_ORDER.length - 1) {
    return step;
  }
  return CONTACT_WIZARD_STEP_ORDER[index + 1];
}

export function previousStep(step: ContactWizardStep): ContactWizardStep {
  const index = CONTACT_WIZARD_STEP_ORDER.indexOf(step);
  if (index <= 0) {
    return step;
  }
  return CONTACT_WIZARD_STEP_ORDER[index - 1];
}

export function isNameComplete(firstName: string, lastName: string): boolean {
  return (firstName ?? '').trim().length > 0 && (lastName ?? '').trim().length > 0;
}

export function canSkipToReview(step: ContactWizardStep, firstName: string, lastName: string): boolean {
  if (step === ContactWizardStep.Role || step === ContactWizardStep.Review) {
    return false;
  }
  return isNameComplete(firstName, lastName);
}

export function nextStepLabel(step: ContactWizardStep): string {
  const following = nextStep(step);
  if (following === step) {
    return '';
  }
  if (following === ContactWizardStep.Review) {
    return 'Review';
  }
  return 'Next: ' + stepTitle(following).toLowerCase();
}
